import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import Button from 'components/Forms/Button/Button';
import { Webline } from 'components/Layout/Webline/Webline';
import { useNotificationBars } from 'connectors/notificationBars/NotificationBars';
import { getCookie } from 'cookies-next';
import { useAuth } from 'hooks/auth/useAuth';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { jwtDecode } from 'jwt-decode';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

const NotificationBars: FC = () => {
    const { t } = useTranslation();
    const notificationBarItems = useNotificationBars();
    const { isUserLoggedIn, user } = useCurrentUserData();
    const [isAdminLoggedInAsUser, setIsAdminLoggedAsUser] = useState(false);
    const { logout } = useAuth();

    useEffect(() => {
        try {
            const accessToken = getCookie('accessToken');
            if (!accessToken) {
                return;
            }
            const decodedAccessToken = jwtDecode<Record<string, any>>(accessToken.toString());
            if ('administratorUuid' in decodedAccessToken && decodedAccessToken.administratorUuid !== null) {
                setIsAdminLoggedAsUser(true);

                return;
            }
            setIsAdminLoggedAsUser(false);
        } catch {
            setIsAdminLoggedAsUser(false);
        }
    }, [isUserLoggedIn]);

    return (
        <>
            {isAdminLoggedInAsUser && (
                <div className="relative z-menu bg-red">
                    <Webline>
                        <div className="font-small flex items-center justify-center text-center font-bold leading-6">
                            <Trans
                                defaultTrans="Warning! You are logged in as a customer with the email {{ email }} <button>Log out</button>"
                                i18nKey="adminLoggedInAsCustomerWarning"
                                values={{ email: user?.email }}
                                components={{
                                    button: (
                                        <Button
                                            size="small"
                                            style={{ marginLeft: '10px' }}
                                            type="button"
                                            variant="secondary"
                                            onClick={logout}
                                        />
                                    ),
                                }}
                            />
                        </div>
                    </Webline>
                </div>
            )}

            {notificationBarItems.map((item, index) => (
                <div
                    key={index}
                    style={{ backgroundColor: item.rgbColor }}
                    className={twJoin(
                        'relative z-menu aspect-[40/11] w-screen items-center justify-center md:aspect-auto md:w-auto',
                        item.webImage !== null ? 'md:block' : 'md:hidden',
                        item.mobileImage !== null ? 'block' : 'hidden',
                    )}
                >
                    <Webline>
                        <div className="font-small flex w-full items-center justify-center text-center font-bold leading-6">
                            {item.url ? (
                                <>
                                    {item.webImage !== null && (
                                        <ExtendedNextLink
                                            passHref
                                            href={item.url as string}
                                            title={t('Notification Bar link')}
                                        >
                                            <div className="hidden aspect-[240/13] w-[1200px] max-w-[100vw] cursor-pointer md:block">
                                                <Image
                                                    fill
                                                    alt={t('Notification Bar Image')}
                                                    sizes="(min-width: 1440px) 1440px, 100vw"
                                                    src={item.webImage.url}
                                                />
                                            </div>
                                        </ExtendedNextLink>
                                    )}
                                    {item.mobileImage !== null && (
                                        <ExtendedNextLink
                                            passHref
                                            href={item.url as string}
                                            title={t('Notification Bar link')}
                                        >
                                            <div className="-mx-5 my-0 block cursor-pointer md:hidden">
                                                <Image
                                                    fill
                                                    alt={t('Notification Bar Image')}
                                                    sizes="(min-width: 1440px) 1440px, 100vw"
                                                    src={item.mobileImage.url}
                                                />
                                            </div>
                                        </ExtendedNextLink>
                                    )}
                                </>
                            ) : (
                                <>
                                    {item.webImage !== null && (
                                        <div className="hidden aspect-[240/13] w-[1200px] max-w-[100vw] cursor-pointer border border-black object-contain md:block">
                                            <Image
                                                fill
                                                alt={t('Notification Bar Image')}
                                                sizes="(min-width: 1440px) 1440px, 100vw"
                                                src={item.webImage.url}
                                            />
                                        </div>
                                    )}
                                    {item.mobileImage !== null && (
                                        <div className="-mx-5 my-0 block cursor-pointer md:hidden">
                                            <Image
                                                fill
                                                alt={t('Notification Bar Image')}
                                                sizes="(min-width: 1440px) 1440px, 100vw"
                                                src={item.mobileImage.url}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Webline>
                </div>
            ))}
        </>
    );
};

export default NotificationBars;
